import { VALUE_CHAIN_AI_AGENT, VALUE_CHAIN_MONITORING_AGENT } from 'constants/general';
import {
  isVcAgentBomSocketInProgressStatus,
  isVcAgentBomSocketProcessingStatus,
} from 'utils/AICreatorHelper';

import type ISessionVCItem from 'store/slices/valueChainMap/interfaces/ISessionVCItem';

export const buildMonitoringStartPayload = (
  subType: string | undefined,
  agentConfigId: number,
) => ({
  subType: subType ?? VALUE_CHAIN_MONITORING_AGENT.MEDIA_SENTIMENT_SUB_TYPE,
  agentConfigId,
});

export const isMonitoringSessionProcessingStatus = (status?: string): boolean => (
  isVcAgentBomSocketInProgressStatus(status)
  || isVcAgentBomSocketProcessingStatus(status)
);

export const isMonitoringSessionDoneStatus = (status?: string): boolean => (
  status === VALUE_CHAIN_AI_AGENT.STATUS_DONE
);

export const isMonitoringSessionType = (sessionType?: string): boolean => (
  sessionType === VALUE_CHAIN_MONITORING_AGENT.REPUTATION_SESSION_TYPE
  || sessionType === VALUE_CHAIN_MONITORING_AGENT.REGULATION_SESSION_TYPE
  || sessionType === VALUE_CHAIN_MONITORING_AGENT.FINANCE_SESSION_TYPE
  || sessionType === VALUE_CHAIN_MONITORING_AGENT.OWNERSHIP_SESSION_TYPE
  || sessionType === VALUE_CHAIN_MONITORING_AGENT.CLIMATE_SESSION_TYPE
);

export const isMonitoringSessionReadyForResults = (session?: ISessionVCItem): boolean => (
  isMonitoringSessionType(session?.type)
  && isMonitoringSessionDoneStatus(session?.status)
);

export const resolveMonitoringSessionAfterStart = (
  session: ISessionVCItem,
  startedSession?: ISessionVCItem,
  fallbackSubType: string = VALUE_CHAIN_MONITORING_AGENT.MEDIA_SENTIMENT_SUB_TYPE,
): ISessionVCItem => ({
  ...session,
  ...startedSession,
  status: startedSession?.status || VALUE_CHAIN_AI_AGENT.STATUS_PROCESSING,
  subType: startedSession?.subType || session.subType || fallbackSubType,
});

export const shouldUseMonitoringMock = (sessionType?: string): boolean => {
  if (sessionType === VALUE_CHAIN_MONITORING_AGENT.REGULATION_SESSION_TYPE) {
    return VALUE_CHAIN_MONITORING_AGENT.REGULATION_MONITORING_USE_MOCK;
  }

  if (sessionType === VALUE_CHAIN_MONITORING_AGENT.FINANCE_SESSION_TYPE) {
    return VALUE_CHAIN_MONITORING_AGENT.FINANCE_MONITORING_USE_MOCK;
  }

  if (sessionType === VALUE_CHAIN_MONITORING_AGENT.OWNERSHIP_SESSION_TYPE) {
    return VALUE_CHAIN_MONITORING_AGENT.OWNERSHIP_MONITORING_USE_MOCK;
  }

  if (sessionType === VALUE_CHAIN_MONITORING_AGENT.CLIMATE_SESSION_TYPE) {
    return VALUE_CHAIN_MONITORING_AGENT.CLIMATE_MONITORING_USE_MOCK;
  }

  return VALUE_CHAIN_MONITORING_AGENT.REPUTATION_MONITORING_USE_MOCK;
};

export const resolveMonitoringActiveAgentKind = (
  sessionType?: string,
):
| 'reputation-monitoring'
| 'regulation-monitoring'
| 'finance-monitoring'
| 'ownership-monitoring'
| 'climate-monitoring' => {
  if (sessionType === VALUE_CHAIN_MONITORING_AGENT.REGULATION_SESSION_TYPE) {
    return 'regulation-monitoring';
  }

  if (sessionType === VALUE_CHAIN_MONITORING_AGENT.FINANCE_SESSION_TYPE) {
    return 'finance-monitoring';
  }

  if (sessionType === VALUE_CHAIN_MONITORING_AGENT.OWNERSHIP_SESSION_TYPE) {
    return 'ownership-monitoring';
  }

  if (sessionType === VALUE_CHAIN_MONITORING_AGENT.CLIMATE_SESSION_TYPE) {
    return 'climate-monitoring';
  }

  return 'reputation-monitoring';
};

export const logMonitoringSessionStartError = (
  sessionId: number | undefined,
  error: unknown,
  meta?: {
    type?: string,
    subType?: string,
  },
): void => {
  const sessionType = meta?.type || VALUE_CHAIN_MONITORING_AGENT.REPUTATION_SESSION_TYPE;

  // eslint-disable-next-line no-console
  console.error(`[${sessionType}] start session failed`, {
    sessionId,
    type: sessionType,
    subType: meta?.subType || VALUE_CHAIN_MONITORING_AGENT.MEDIA_SENTIMENT_SUB_TYPE,
    error,
  });
};
